import type { ArticlePublicPathInput } from '~/constants/routes/unified';

import { useIaChrome } from '~/composables/unified/useIaChrome';
import {
  COACH_ROUTE_BADGE,
  COACH_ROUTE_CDP,
  COACH_ROUTE_CDP_SHARE_SETTINGS,
  COACH_ROUTE_COURSE,
  COACH_ROUTE_EXPLORE,
  COACH_ROUTE_HUB,
  COACH_ROUTE_INSTITUTION,
  COACH_ROUTE_JOB,
  COACH_ROUTE_LEARN,
  COACH_ROUTE_LEARN_COLLECTION,
  COACH_ROUTE_VIDEO,
  coachArticlePath,
} from '~/constants/routes/coach';
import {
  articlePublicPath,
  UNIFIED_ROUTE_CAREERS,
  UNIFIED_ROUTE_CDP,
  UNIFIED_ROUTE_CDP_SHARE_SETTINGS,
  UNIFIED_ROUTE_COURSE_DETAIL,
  UNIFIED_ROUTE_EXPLORE,
  UNIFIED_ROUTE_HUB_DETAIL,
  UNIFIED_ROUTE_INSTITUTION_DETAIL,
  UNIFIED_ROUTE_JOB_DETAIL,
  UNIFIED_ROUTE_LEARN,
  UNIFIED_ROUTE_LEARN_ASSET,
  UNIFIED_ROUTE_LEARN_COLLECTION,
  UNIFIED_ROUTE_LEARN_IA_BADGE,
  UNIFIED_ROUTE_VIDEO_DETAIL,
} from '~/constants/routes/unified';

/**
 * Where the IA's Learn and Explore link to. A signed-in student reads them inside the coach shell,
 * so their links stay in it (`/coach/learn?category=…`, `/coach/jobs/<slug>`) and opening one never
 * takes the shell down; everyone else reads the public pages and stays on them.
 *
 * Every page a place opens has its page in the shell: the indexes and their categories, a badge, an
 * experience, a piece of self-discovery, a job, an institution, a course, an industry hub, an
 * article, a video, and the student's Replay and its share settings. The career library has none,
 * so inside the shell its link is Explore's Industries, which lists the same hubs.
 */
export const useIaPlaces = () => {
  const inShell = useIaChrome();
  const learn = inShell ? COACH_ROUTE_LEARN : UNIFIED_ROUTE_LEARN;
  const explore = inShell ? COACH_ROUTE_EXPLORE : UNIFIED_ROUTE_EXPLORE;
  const learnCategory = (category: string) => `${learn}?category=${category}`;
  const exploreCategory = (category: string) => `${explore}?category=${category}`;

  return {
    inShell,
    learn,
    learnCategory,
    learnVideos: `${learnCategory('advice')}&type=videos`,
    badge: (slug: string) => (inShell ? COACH_ROUTE_BADGE(slug) : UNIFIED_ROUTE_LEARN_IA_BADGE(slug)),
    collection: (slug: string) => (inShell ? COACH_ROUTE_LEARN_COLLECTION(slug) : UNIFIED_ROUTE_LEARN_COLLECTION(slug)),
    asset: (slug: string) => (inShell ? `${COACH_ROUTE_LEARN}/asset/${slug}` : UNIFIED_ROUTE_LEARN_ASSET(slug)),
    article: (article: ArticlePublicPathInput) => (inShell ? coachArticlePath(article) : articlePublicPath(article)),
    video: (video: { slug: string; hubSlug?: string }) => (inShell ? COACH_ROUTE_VIDEO(video) : UNIFIED_ROUTE_VIDEO_DETAIL(video)),
    explore,
    exploreCategory,
    job: (slug: string) => (inShell ? COACH_ROUTE_JOB(slug) : `${UNIFIED_ROUTE_JOB_DETAIL}/${slug}`),
    institution: (slug: string) => (inShell ? COACH_ROUTE_INSTITUTION(slug) : UNIFIED_ROUTE_INSTITUTION_DETAIL(slug)),
    course: (id: string) => (inShell ? COACH_ROUTE_COURSE(id) : UNIFIED_ROUTE_COURSE_DETAIL(id)),
    hub: (slug: string) => (inShell ? COACH_ROUTE_HUB(slug) : UNIFIED_ROUTE_HUB_DETAIL(slug)),
    careers: inShell ? exploreCategory('industries') : UNIFIED_ROUTE_CAREERS,
    cdp: inShell ? COACH_ROUTE_CDP : UNIFIED_ROUTE_CDP,
    cdpShareSettings: inShell ? COACH_ROUTE_CDP_SHARE_SETTINGS : UNIFIED_ROUTE_CDP_SHARE_SETTINGS,
  };
};
